import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  Cell
} from 'recharts';
import {
  Building2, Plus, ArrowLeft, TrendingUp, TrendingDown, Minus,
  Users, Target, Sparkles, ChevronDown, Calendar, Filter, MoreVertical,
  Edit2, Trash2, X, Check, Layers, BarChart3, Activity
} from 'lucide-react';

const COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#84cc16'];

function DepartmentalDrillDownPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [departmentTrend, setDepartmentTrend] = useState(null);
  const [departmentMetrics, setDepartmentMetrics] = useState(null);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState('30d');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [viewMode, setViewMode] = useState('comparison'); // comparison, detail, trend

  useEffect(() => {
    loadDepartments();
    loadComparison();
  }, [dateRange]);

  const getDateRange = useCallback(() => {
    const end = new Date();
    const start = new Date();
    
    switch(dateRange) {
      case '7d':
        start.setDate(end.getDate() - 7);
        break;
      case '30d':
        start.setDate(end.getDate() - 30);
        break;
      case '90d':
        start.setDate(end.getDate() - 90);
        break;
      case '1y':
        start.setFullYear(end.getFullYear() - 1);
        break;
      default:
        start.setDate(end.getDate() - 30);
    }
    
    return { start, end };
  }, [dateRange]);

  const loadDepartments = async () => {
    try {
      const response = await axios.get('/departments/');
      setDepartments(response.data);
    } catch (err) {
      console.error('Failed to load departments:', err);
    }
  };

  const loadComparison = async () => {
    try {
      setLoading(true);
      const { start, end } = getDateRange();
      
      const response = await axios.get('/departments/comparison/all', {
        params: {
          start: start.toISOString(),
          end: end.toISOString()
        }
      });
      
      setComparison(response.data);
      setError('');
    } catch (err) {
      console.error('Failed to load comparison:', err);
      setError('Failed to load department comparison');
    } finally {
      setLoading(false);
    }
  };

  const loadDepartmentDetail = async (deptId) => {
    try {
      const { start, end } = getDateRange();
      
      const [trendRes, metricsRes] = await Promise.all([
        axios.get(`/departments/${deptId}/trend`, {
          params: {
            start: start.toISOString(),
            end: end.toISOString(),
            interval: 'monthly'
          }
        }),
        axios.get(`/departments/${deptId}/metrics`, {
          params: {
            start: start.toISOString(),
            end: end.toISOString()
          }
        })
      ]);
      
      setDepartmentTrend(trendRes.data);
      setDepartmentMetrics(metricsRes.data);
    } catch (err) {
      console.error('Failed to load department details:', err);
    }
  };

  const selectDepartment = (dept) => {
    setSelectedDepartment(dept);
    setViewMode('detail');
    loadDepartmentDetail(dept.department_id);
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score) => {
    if (score >= 70) return 'bg-green-100';
    if (score >= 50) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const getDeltaIcon = (delta) => {
    if (delta > 0) return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (delta < 0) return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  // Prepare radar chart data
  const getRadarData = () => {
    if (!comparison?.departments) return [];
    
    return comparison.departments.slice(0, 5).map(dept => ({
      name: dept.department_name,
      authenticity: dept.authenticity_score,
      accountability: dept.accountability_score,
      community: dept.community_score
    }));
  };

  if (loading && !comparison) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading departmental data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900" data-testid="page-title">
                  Departmental Drill-Down
                </h1>
                <p className="text-sm text-gray-500">Analyze trust scores by department or segment</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Date Range */}
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  className="pl-10 pr-8 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 appearance-none bg-white cursor-pointer text-sm"
                  data-testid="date-range-selector"
                >
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                  <option value="90d">Last 90 Days</option>
                  <option value="1y">Last Year</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
              </div>

              {/* Add Department Button */}
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
                data-testid="add-department-button"
              >
                <Plus className="w-4 h-4" />
                Add Department
              </button>
            </div>
          </div>

          {/* View Mode Tabs */}
          <div className="flex gap-2 mt-4 border-t border-gray-100 pt-4">
            <button
              onClick={() => { setViewMode('comparison'); setSelectedDepartment(null); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition text-sm font-medium ${
                viewMode === 'comparison' 
                  ? 'bg-purple-100 text-purple-700' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid="comparison-tab"
            >
              <BarChart3 className="w-4 h-4" />
              Comparison
            </button>
            <button
              onClick={() => setViewMode('detail')}
              disabled={!selectedDepartment}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition text-sm font-medium ${
                viewMode === 'detail' 
                  ? 'bg-purple-100 text-purple-700' 
                  : 'text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed'
              }`}
              data-testid="detail-tab"
            >
              <Layers className="w-4 h-4" />
              Detail View
              {selectedDepartment && (
                <span className="ml-1 text-xs bg-purple-200 px-2 py-0.5 rounded-full">
                  {selectedDepartment.department_name}
                </span>
              )}
            </button>
            <button
              onClick={() => setViewMode('trend')}
              disabled={!selectedDepartment}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition text-sm font-medium ${
                viewMode === 'trend' 
                  ? 'bg-purple-100 text-purple-700' 
                  : 'text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed'
              }`}
              data-testid="trend-tab"
            >
              <Activity className="w-4 h-4" />
              Trend Analysis
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Comparison View */}
        {viewMode === 'comparison' && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Building2 className="w-5 h-5 text-purple-600" />
                  </div>
                  <span className="text-sm text-gray-500">Total Departments</span>
                </div>
                <p className="text-3xl font-bold text-gray-900">{departments.length}</p>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Target className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="text-sm text-gray-500">Org Average</span>
                </div>
                <p className={`text-3xl font-bold ${getScoreColor(comparison?.organization_average || 0)}`}>
                  {comparison?.organization_average?.toFixed(1) || '—'}
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-sm text-gray-500">Top Performer</span>
                </div>
                <p className="text-lg font-bold text-gray-900 truncate">
                  {comparison?.top_performer || '—'}
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <TrendingDown className="w-5 h-5 text-amber-600" />
                  </div>
                  <span className="text-sm text-gray-500">Needs Attention</span>
                </div>
                <p className="text-lg font-bold text-gray-900 truncate">
                  {comparison?.needs_attention || '—'}
                </p>
              </div>
            </div>

            {/* Department Comparison Chart */}
            <div className="bg-white rounded-xl shadow-lg p-6 mb-8" data-testid="comparison-chart">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Department Trust Score Comparison</h2>
              
              {comparison?.departments?.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={comparison.departments} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" domain={[0, 100]} />
                    <YAxis 
                      type="category" 
                      dataKey="department_name" 
                      width={120}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip 
                      formatter={(value) => [value?.toFixed(1), '']}
                      contentStyle={{ 
                        background: 'white', 
                        border: 'none', 
                        borderRadius: '12px',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                      }}
                    />
                    <Legend />
                    <Bar dataKey="trust_score" name="Trust Score" fill="#8b5cf6" radius={[0, 4, 4, 0]}>
                      {comparison.departments.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={COLORS[index % COLORS.length]}
                          cursor="pointer"
                          onClick={() => selectDepartment(entry)}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center bg-gray-50 rounded-xl">
                  <div className="text-center">
                    <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-2">No department data available</p>
                    <p className="text-sm text-gray-400">Create departments and assign metrics to see comparisons</p>
                  </div>
                </div>
              )}
            </div>

            {/* Pillar Comparison Radar Chart */}
            {comparison?.departments?.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <div className="bg-white rounded-xl shadow-lg p-6" data-testid="radar-chart">
                  <h2 className="text-xl font-bold text-gray-900 mb-6">Pillar Breakdown by Department</h2>
                  <ResponsiveContainer width="100%" height={350}>
                    <RadarChart data={[
                      { pillar: 'Authenticity', ...Object.fromEntries(comparison.departments.slice(0, 5).map(d => [d.department_name, d.authenticity_score])) },
                      { pillar: 'Accountability', ...Object.fromEntries(comparison.departments.slice(0, 5).map(d => [d.department_name, d.accountability_score])) },
                      { pillar: 'Community', ...Object.fromEntries(comparison.departments.slice(0, 5).map(d => [d.department_name, d.community_score])) }
                    ]}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="pillar" />
                      <PolarRadiusAxis domain={[0, 100]} />
                      {comparison.departments.slice(0, 5).map((dept, idx) => (
                        <Radar
                          key={dept.department_id}
                          name={dept.department_name}
                          dataKey={dept.department_name}
                          stroke={COLORS[idx % COLORS.length]}
                          fill={COLORS[idx % COLORS.length]}
                          fillOpacity={0.2}
                        />
                      ))}
                      <Legend />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>

                {/* Department Cards */}
                <div className="bg-white rounded-xl shadow-lg p-6" data-testid="department-list">
                  <h2 className="text-xl font-bold text-gray-900 mb-6">Department Details</h2>
                  <div className="space-y-3 max-h-[350px] overflow-y-auto">
                    {comparison.departments.map((dept, idx) => (
                      <div
                        key={dept.department_id}
                        onClick={() => selectDepartment(dept)}
                        className="p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 cursor-pointer transition"
                        data-testid={`department-card-${dept.department_id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                            />
                            <span className="font-medium text-gray-900">{dept.department_name}</span>
                          </div>
                          <span className={`text-lg font-bold ${getScoreColor(dept.trust_score)}`}>
                            {dept.trust_score.toFixed(1)}
                          </span>
                        </div>
                        <div className="flex gap-4 text-sm text-gray-500">
                          <span>Auth: {dept.authenticity_score?.toFixed(1)}</span>
                          <span>Acc: {dept.accountability_score?.toFixed(1)}</span>
                          <span>Com: {dept.community_score?.toFixed(1)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Detail View */}
        {viewMode === 'detail' && selectedDepartment && (
          <>
            {/* Department Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-6 mb-8 text-white shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold">{selectedDepartment.department_name}</h2>
                  <p className="text-purple-100 mt-1">
                    {selectedDepartment.department_type || 'Department'} • {selectedDepartment.metric_count || 0} metrics
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-purple-100 text-sm">Trust Score</p>
                  <p className="text-4xl font-bold">{selectedDepartment.trust_score?.toFixed(1)}</p>
                </div>
              </div>
            </div>

            {/* Pillar Scores */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                  </div>
                  <span className="text-gray-500">Authenticity</span>
                </div>
                <p className={`text-3xl font-bold ${getScoreColor(selectedDepartment.authenticity_score)}`}>
                  {selectedDepartment.authenticity_score?.toFixed(1)}
                </p>
                <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-purple-500 rounded-full transition-all"
                    style={{ width: `${selectedDepartment.authenticity_score}%` }}
                  />
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Target className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-gray-500">Accountability</span>
                </div>
                <p className={`text-3xl font-bold ${getScoreColor(selectedDepartment.accountability_score)}`}>
                  {selectedDepartment.accountability_score?.toFixed(1)}
                </p>
                <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500 rounded-full transition-all"
                    style={{ width: `${selectedDepartment.accountability_score}%` }}
                  />
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="text-gray-500">Community</span>
                </div>
                <p className={`text-3xl font-bold ${getScoreColor(selectedDepartment.community_score)}`}>
                  {selectedDepartment.community_score?.toFixed(1)}
                </p>
                <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-500 rounded-full transition-all"
                    style={{ width: `${selectedDepartment.community_score}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Metric Breakdown */}
            {departmentMetrics && (
              <div className="bg-white rounded-xl shadow-lg p-6" data-testid="metrics-breakdown">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Metric Breakdown</h3>
                
                {Object.keys(departmentMetrics.pillar_breakdown || {}).length > 0 ? (
                  <div className="space-y-6">
                    {Object.entries(departmentMetrics.pillar_breakdown).map(([pillarKey, pillarData]) => (
                      <div key={pillarKey} className="border border-gray-200 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-3 capitalize">{pillarData.name}</h4>
                        <div className="space-y-2">
                          {pillarData.metrics.map((metric) => (
                            <div 
                              key={metric.metric_id}
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                            >
                              <div>
                                <p className="font-medium text-gray-900">{metric.metric_name}</p>
                                <p className="text-sm text-gray-500">Weight: {metric.weight}</p>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-gray-900">
                                  {metric.normalized_value?.toFixed(1) || metric.raw_value?.toFixed(1) || '—'}
                                </p>
                                <p className="text-sm text-gray-500">Raw: {metric.raw_value?.toFixed(2) || '—'}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-gray-50 rounded-xl">
                    <p className="text-gray-500">No metric data available for this department</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Trend View */}
        {viewMode === 'trend' && selectedDepartment && departmentTrend && (
          <>
            <div className="bg-white rounded-xl shadow-lg p-6 mb-8" data-testid="trend-chart">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                Trust Score Trend - {selectedDepartment.department_name}
              </h2>
              
              {departmentTrend.trend_data?.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={departmentTrend.trend_data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getMonth() + 1}/${date.getFullYear().toString().slice(2)}`;
                      }}
                      stroke="#9ca3af"
                    />
                    <YAxis domain={[0, 100]} stroke="#9ca3af" />
                    <Tooltip 
                      labelFormatter={(value) => new Date(value).toLocaleDateString()}
                      formatter={(value) => [value?.toFixed(1) || '—', '']}
                      contentStyle={{ 
                        background: 'white', 
                        border: 'none', 
                        borderRadius: '12px',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                      }}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="trust_score" 
                      stroke="#8b5cf6" 
                      strokeWidth={3}
                      name="Trust Score"
                      dot={{ fill: '#8b5cf6', strokeWidth: 2 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="authenticity" 
                      stroke="#a855f7" 
                      strokeWidth={2}
                      name="Authenticity"
                      dot={false}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="accountability" 
                      stroke="#10b981" 
                      strokeWidth={2}
                      name="Accountability"
                      dot={false}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="community" 
                      stroke="#3b82f6" 
                      strokeWidth={2}
                      name="Community"
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center bg-gray-50 rounded-xl">
                  <div className="text-center">
                    <Activity className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No trend data available</p>
                    <p className="text-sm text-gray-400">Historical data will appear once metrics are collected over time</p>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </main>

      {/* Create Department Modal */}
      {showCreateModal && (
        <CreateDepartmentModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            loadDepartments();
            loadComparison();
          }}
        />
      )}
    </div>
  );
}

// Create Department Modal Component
function CreateDepartmentModal({ onClose, onCreated }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    department_type: 'department'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Department name is required');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      await axios.post('/departments/', formData);
      onCreated();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create department');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="create-department-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Create Department</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="e.g., Marketing, Sales, Operations"
              data-testid="department-name-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            <select
              value={formData.department_type}
              onChange={(e) => setFormData({ ...formData, department_type: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              data-testid="department-type-select"
            >
              <option value="department">Department</option>
              <option value="region">Region</option>
              <option value="product_line">Product Line</option>
              <option value="team">Team</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Optional description..."
              data-testid="department-description-input"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50"
              data-testid="create-department-submit"
            >
              {loading ? 'Creating...' : 'Create Department'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DepartmentalDrillDownPage;
